import { cls } from "@wss/cls";
import React, { useState } from "react";
import { connect } from "react-redux";
import { Image } from "@wss/image";
import SaleBadgeAndPricing from "~/common/SaleBadgeAndPricing";
import Stars from "~/common/Stars";
import { Product, SelectedProduct } from "~/common/Store/models/ProductListing";
import { RootState, UserState } from "~/common/Store/models/RootReadonly";
import AddToCartButton, {
  ProductListingView,
} from "~/productListing/ProductBox/AddToCart";
import PlatinumPlusExclusive from "~/productListing/ProductBox/PlatinumPlusExclusive";
import StandardPrice from "~/productListing/ProductBox/Pricing/Price";
import { PlusLink } from "@clarkinc/zest-design-system/ui";
import isNonExclusiveForPlatinumPlusProduct from "~/productListing/ProductBox/isNonExclusivePlatinumPlusProduct";

interface Props {
  product: Product;
  index: number;
  user: UserState;
  hasCustomizableProductsOverride?: boolean;
  mounted: boolean;
  isItemAlreadyInCart?: boolean;
  lazyLoadImage?: boolean;
  productListingView?: ProductListingView;
  outOfStockStyle?: string;
}

const TopProductsContent = ({
  product,
  user,
  hasCustomizableProductsOverride = false,
  isItemAlreadyInCart = false,
  lazyLoadImage,
  productListingView = "topProductsCarousel",
}: Props) => {
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct>({
    ...product,
    activeSale: product.price.displaySalePrice,
    isOutlet: product.outletAttributes && product.outletAttributes.isOutlet,
    price: {
      ...product.price,
      originalPrice:
        product.outletAttributes && product.outletAttributes.originalPrice,
    },
  });

  const {
    descriptionHtml,
    primaryImagePath,
    secondaryImagePath,
    isWebstaurantPlusEligible,
    oneToFiveStarRating,
    overallRating,
    link,
    customerExclusivity,
  } = product;

  return (
    <div className="flex flex-col h-full">
      <div
        className="relative group border-solid border-transparent border-6 m-0 max-w-full"
        data-testid="carousel"
      >
        <a
          className="block"
          href={link}
          data-testid="itemLink"
          data-label={product.feedIdentifier}
          data-action="click"
        >
          <Image
            alt=""
            src={primaryImagePath}
            className="inset-0 w-full h-auto"
            width={1}
            height={1}
            loading={lazyLoadImage ? "lazy" : "eager"}
          />
          {secondaryImagePath && (
            <Image
              alt=""
              src={secondaryImagePath}
              width={1}
              height={1}
              className="absolute max-w-full opacity-0 hover:opacity-100 inset-0 w-full transition ease-in-out duration-300"
              loading="lazy"
              fetchpriority="low"
            />
          )}
          <span
            className="block font-semibold text-sm-1/2 leading-none my-2-1/2 max-h-10 min-h-10 overflow-hidden text-center mb-0"
            data-testid="itemDescription"
          >
            {descriptionHtml}
          </span>
        </a>
      </div>
      <div
        className={cls(
          "flex align-middle justify-center text-center mb-2.5",
          oneToFiveStarRating === 0 && !isWebstaurantPlusEligible && "mt-2.5",
          oneToFiveStarRating === 0 && isWebstaurantPlusEligible && "mt-1",
        )}
      >
        <div className="flex justify-center mt-1">
          {isWebstaurantPlusEligible && (
            <PlusLink className="mr-0.5 mt-0.5" data-testid={`plusLink`} />
          )}
          {!!overallRating && <Stars rating={overallRating} size="large" />}
        </div>
      </div>
      <div className="flex flex-col justify-start flex-auto mt-0 mb-4 overflow-hidden relative text-center lt:mt-0">
        <PlatinumPlusExclusive
          user={user}
          customerExclusivity={customerExclusivity}
          extraClassNames={cls(
            "rounded text-gray-800",
            selectedProduct.activeSale && "mb-2",
          )}
        />
        {selectedProduct.activeSale && (
          <SaleBadgeAndPricing
            price={selectedProduct.price.price}
            dataTestId="top-products-carousel-sale"
            salePricingClassNames="p-0 pb-1 flex items-center text-xs text-gray-800 mb-1 justify-center"
            saleBadgeExtraClassNames="px-1 mr-1"
          />
        )}
        <StandardPrice
          price={
            selectedProduct.activeSale
              ? selectedProduct.price.salePrice
              : selectedProduct.price.price
          }
          unitOfMeasure={selectedProduct.unitOfMeasure}
          productListingView={productListingView}
          isOutlet={selectedProduct.isOutlet}
          textSize="custom"
          className="text-lg"
        />
      </div>
      {isNonExclusiveForPlatinumPlusProduct(customerExclusivity, user) && (
        <div>
          <AddToCartButton
            {...{
              product,
              user,
              setSelectedProduct,
              selectedProduct,
              isCarouselProductBox: true,
              isItemAlreadyInCart,
              hasCustomizableProductsOverride,
              productListingView,
              outOfStockStyle:
                "font-semibold text-base leading-none overflow-hidden relative text-center text-red-400 xxxs:ml-1",
            }}
          />
        </div>
      )}
    </div>
  );
};

export const mapStateToProps = (state: RootState) => ({ user: state.user });

export default connect(mapStateToProps)(TopProductsContent);
